<template>
  <div id="banner_right_section" class="flex flex-row items-center gap-4">
    <div id="campaign_statistics" class="flex flex-row gap-3 items-center">
      <div
        v-for="(campaign, index) in campaignStatistics"
        :key="`${campaign?.id}_campaign_${index}`"
        class="flex flex-col py-[6px] px-3 items-center"
      >
        <span class="text-sm opacity-60 font-semibold">{{
          campaign?.text
        }}</span>
        <span class="text-base font-bold">{{ campaign?.value }}</span>
      </div>
    </div>

    <div
      v-if="shouldShowCampaignType"
      id="campaign_type"
      class="relative"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <div
        class="flex flex-row items-center justify-center h-10 gap-2 container_8"
        :class="getContainerClass"
        @click="handleSkipCooldown"
      >
        <img
          class="size-5"
          :src="
            showSkipCooldown
              ? imagePath.dark.action.resumeIconWhite
              : callImagePath.phoneNumbersSelectedIcon
          "
          alt="call_icon"
        />
        <span class="text-base">{{ currentDisplayText }}</span>
      </div>
    </div>

    <BannerCallCampaignStatus :campaign-details="campaignDetails" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { capitalizeFirstLetter } from "@/src/utils/utils.js";
import { toGetCallCampaignById } from "@/src/utils/callsUtils.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";
import BannerCallCampaignStatus from "@/src/views/calls/call_campaigns/campaign_banner/BannerCallCampaignStatus.vue";
import imagePath from "@/src/paths/imagePath.js";
import callImagePath from "@/src/paths/callImagePath.js";
import { callCampaignStatuses } from "@/src/constants/callConstants.js";

const countdownTimer = ref(null);
const countdown = ref(0);
const showSkipCooldown = ref(false);
const processingNextCall = ref(false);

const campaignDetails = computed(
  () => store.getters["callsCampaign/campaignDetails"],
);

const callActive = computed(() => store.getters["calls/isCallConnected"]);

const isCampaignActive = computed(
  () => store.getters["callsCampaign/isCurrentCampaign"],
);

const callOngoing = computed(() => store.getters["callsCampaign/callOngoing"]);

const hasActiveCall = computed(() => store.getters["calls/hasActiveCall"]);

const shouldShowCampaignType = computed(() => {
  return isCampaignActive.value || callOngoing.value;
});

const getContainerClass = computed(() => {
  if (countdown.value > 0 && !callOngoing.value) {
    return "rounded-lg px-6 w-[216px] hover:cursor-pointer";
  }
  return "rounded-3xl px-4";
});

const displayText = computed(() => {
  if (callActive.value && callOngoing.value) {
    return "Connected";
  }

  if (countdown.value > 0 && !callOngoing.value) {
    const totalCallsAttempted =
      store.getters["callsCampaign/totalCallsAttempted"];
    const totalCalls = store.getters["callsCampaign/totalCampaignCalls"];

    if (totalCallsAttempted !== totalCalls) {
      return `Next call in ${countdown.value} secs`;
    }
  }

  if (campaignDetails.value?.campaign_type === "voice_agents") {
    return "Agent Dialing..";
  }

  const campaignType = capitalizeFirstLetter(
    campaignDetails.value?.campaign_type,
  );
  return `${campaignType} Dialing..`;
});

const currentDisplayText = computed(() => {
  if (countdown.value > 0 && showSkipCooldown.value && !callOngoing.value) {
    return "Skip Cooldown";
  }
  return displayText.value;
});

const campaignStatistics = computed(() => [
  {
    id: "total",
    text: "Total",
    value: campaignDetails.value?.total_contacts || 0,
  },
  {
    id: "pending",
    text: "Pending",
    value: campaignDetails.value?.pending_calls || 0,
  },
  {
    id: "answered",
    text: "Answered",
    value: campaignDetails.value?.answered_calls || 0,
  },
  {
    id: "skipped",
    text: "Unsuccessful",
    value: campaignDetails.value?.skipped_calls || 0,
  },
  {
    id: "voicemails",
    text: "Voicemails",
    value: campaignDetails.value?.voicemail_calls || 0,
  },
]);

const handleMouseEnter = () => {
  if (countdown.value > 0 && !callOngoing.value) {
    showSkipCooldown.value = true;
  }
};

const handleMouseLeave = () => {
  showSkipCooldown.value = false;
};

const safeProcessNextCall = async () => {
  if (processingNextCall.value) {
    return;
  }
  try {
    processingNextCall.value = true;
    await store.dispatch("callsCampaign/processNextCall");
  } catch (error) {
    console.error("Error processing next call:", error);
  } finally {
    processingNextCall.value = false;
  }
};

const handleSkipCooldown = () => {
  try {
    if (countdown.value > 0 && !callOngoing.value) {
      clearInterval(countdownTimer.value);
      countdown.value = 0;
      store.dispatch("callsCampaign/setSkipCooldown", true);
      store.dispatch("callsCampaign/setDrawerVisible", false);
      safeProcessNextCall().then(() => {
        store.dispatch("callsCampaign/setSkipCooldown", false);
      });
    }
  } catch (error) {
    console.error("An error occurred while handling skip cooldown: ", error);
  }
};

const startCountdown = () => {
  try {
    const campaignStatus = store.getters["callsCampaign/campaignStatus"];
    if (campaignStatus === callCampaignStatuses.PAUSED) {
      return;
    }
    if (!callOngoing.value) {
      countdown.value = store.getters["callsCampaign/campaignCooldownTime"];

      if (countdownTimer.value) {
        clearInterval(countdownTimer.value);
      }

      countdownTimer.value = setInterval(() => {
        if (hasActiveCall.value) {
          return;
        }
        if (countdown.value > 0 && !callOngoing.value) {
          countdown.value--;
        } else {
          clearInterval(countdownTimer.value);
          countdown.value = 0;
          if (!store.getters["callsCampaign/isDrawerVisible"]) {
            safeProcessNextCall();
          }
          return;
        }
      }, 1000);
    }
  } catch (error) {
    console.error("An error occurred while starting the countdown: ", error);
  }
};

const handleCallLogAdded = async () => {
  try {
    const updatedCampaign = await toGetCallCampaignById(
      campaignDetails.value?.id,
    );
    if (updatedCampaign) {
      await store.dispatch(
        "callsCampaign/updateCampaignDetails",
        updatedCampaign,
      );
    }
    startCountdown();
  } catch (error) {
    console.error("Error fetching campaign details:", error);
  }
};

const stopCountDown = () => {
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value);
  }
  countdownTimer.value = null;
  countdown.value = 0;
  processingNextCall.value = false;
};

const handleCampaignNextCall = async () => {
  try {
    if (countdown.value === 0) {
      await safeProcessNextCall();
    }
  } catch (error) {
    console.error("Error Starting Next campaign call", error);
  }
};

onMounted(() => {
  eventBus.on("campaign-drawer-off", handleCampaignNextCall);
  eventBus.on("campaign-next-step", handleCallLogAdded);
  eventBus.on("campaign-status-update", handleCallLogAdded);
  eventBus.on("stop-count-down", stopCountDown);
});

onUnmounted(() => {
  eventBus.off("campaign-drawer-off", handleCampaignNextCall);
  eventBus.off("campaign-next-step", handleCallLogAdded);
  eventBus.off("campaign-status-update", handleCallLogAdded);
  eventBus.off("stop-count-down", stopCountDown);
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value);
  }
});
</script>
